/** Client-side OMD product cache (per browser tab, ~24h TTL). */
const OMD_PRODUCT_CACHE_TTL_MS = 24 * 60 * 60 * 1000;

type OmdProductCacheEntry = {
  product: unknown;
  expiresAtMs: number;
};

const productCache = new Map<string, OmdProductCacheEntry>();
const inflightFetches = new Map<string, Promise<unknown>>();

export function getOmdProductCacheKey(
  instanceId: string | undefined,
  supplierSlug: string,
  supplierPid: string,
): string {
  return `${instanceId ?? '_'}:${supplierSlug}:${supplierPid}`;
}

export function getCachedOmdProduct(key: string): unknown | null {
  const entry = productCache.get(key);
  if (!entry) return null;
  if (entry.expiresAtMs <= Date.now()) {
    productCache.delete(key);
    return null;
  }
  return entry.product;
}

export function setCachedOmdProduct(key: string, product: unknown): void {
  productCache.set(key, {
    product,
    expiresAtMs: Date.now() + OMD_PRODUCT_CACHE_TTL_MS,
  });
}

/** Dedupe concurrent fetches for the same cache key; stores successful results. */
export function getOrFetchOmdProduct(
  key: string,
  fetchProduct: () => Promise<unknown>,
): Promise<unknown> {
  const cached = getCachedOmdProduct(key);
  if (cached) return Promise.resolve(cached);

  const inflight = inflightFetches.get(key);
  if (inflight) return inflight;

  const promise = fetchProduct()
    .then((product) => {
      setCachedOmdProduct(key, product);
      return product;
    })
    .finally(() => {
      inflightFetches.delete(key);
    });

  inflightFetches.set(key, promise);
  return promise;
}

function deleteCacheKeysWithPrefix(prefix: string): void {
  for (const key of productCache.keys()) {
    if (key.startsWith(prefix)) productCache.delete(key);
  }
  for (const key of inflightFetches.keys()) {
    if (key.startsWith(prefix)) inflightFetches.delete(key);
  }
}

export function clearOmdProductClientCache(instanceId?: string): void {
  if (!instanceId) {
    productCache.clear();
    inflightFetches.clear();
    return;
  }
  deleteCacheKeysWithPrefix(`${instanceId}:`);
}
